

import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue'
import { useStore } from 'vuex'
import { useQuasar } from 'quasar'
import useContentModule, { Mutation } from '@/pages/composables/useContentModule'
import { CtClient } from '@/models/interfaces/content-type'
import { CtSupplier } from '@/models/interfaces/content-type'
import AddIcon from '@/components/Icons/AddIcon.vue'
import NavigationTabBar from '@/layouts/components/NavigationTabBar.vue'
import NavigationTabBarItem from '@/layouts/components/NavigationTabBarItem.vue'
// import ClientDetails from '@/pages/Channels/components/ClientDetails.vue'
import { useRouter } from 'vue-router'
import supplierDetails from '@/pages/WIP/components/SupplierDetails.vue'

import SelectHeader from '@/components/SelectHeader.vue'
import { DirectrEvent } from '@/services/directr'

enum FormInputType {
  String = 'string',
  Number = 'number',
  Object = 'object',
  Boolean = 'boolean',
  Toggle = 'toggle',
  Array = 'array',
  'Multi-select' = 'multi-select',
  Select = 'select',
  Tag = 'tag',
  'Link-list' = 'link-list',
  Link = 'link',
  Placeholder = 'placeholder',
  Nullable = 'NULL',
}

function getTypeDefault (type: string) {
  switch (type) {
    case FormInputType.String:
      return ''
    case FormInputType.Number:
      return 0
    case FormInputType.Array:
    case FormInputType.Select:
    case FormInputType.Tag:
      return []
    case FormInputType.Object:
      return {}
    case FormInputType['Link-list']:
    case FormInputType.Link:
    case FormInputType.Placeholder:
    case FormInputType.Nullable:
      return null
    case FormInputType.Boolean:
      return false
    default:
      return null
  }
}

export default defineComponent({
  name: 'SupplierOverview',
  props: {
    supplierId: {
      type: String,
      required: false,
    },
    supplierCatalogueId: {
      type: String,
      required: false,
    }
  },
  components: { NavigationTabBarItem, NavigationTabBar, AddIcon, supplierDetails, SelectHeader },
  setup (props) {
    const $store = useStore()
    const $q = useQuasar()
    const $directr = $store.getters['Services/Directr']
    const $router = useRouter()

    const state = reactive({
      activeTab: null,
    })

    const { contentType, model, active, items: suppliers, setActive, fetch, loading, mutate, sortOrder, requestHandler } = useContentModule<CtSupplier>('Supplier', $store)

    onMounted(() => {
      $directr.listen(DirectrEvent['Create/Supplier'], (event) => {
        if (!event?.payload) return
        mutate(event?.payload, Mutation.create)
      })
      $directr.listen(DirectrEvent['Update/Supplier'], (event) => {
        if (!event?.payload) return
        mutate(event?.payload, Mutation.update)
      })
    })

    const setActiveSupplier = ($event) => {
      setActive(suppliers.value.find(c => c.sys.id === $event))
    }

    void fetch([], { limit: 100 }).catch(console.error).then(() => {
      watch(() => props.supplierId, (newValue) => {
        if (newValue) {
          setActiveSupplier(newValue)
        } else if (suppliers.value?.length) {
          const firstSupplierId = suppliers.value?.[0].sys.id
          if (firstSupplierId) void $router.push({ name: 'Suppliers', params: { supplierId: firstSupplierId, channelId: props.supplierCatalogueId } })
        }
      })
      watch(() => active.value, (newValue) => {
        if (!newValue) return
        if (newValue?.sys?.id !== props.supplierId) {
          void $router.push({ name: 'Suppliers', params: { supplierId: newValue.sys.id, channelId: props.supplierCatalogueId } })
        }
      })
      if (props.supplierId) {
        setActiveSupplier(props.supplierId)
      } else if (suppliers.value?.length) {
        const firstSupplierId = suppliers.value?.[0].sys.id
        if (firstSupplierId) void $router.replace({ name: 'Suppliers', params: { supplierId: firstSupplierId, channelId: props.supplierCatalogueId } })
      }
    })

    const onSupplierCatalogueClick = () => {
      if (props.supplierCatalogueId) {
        void $router.push({ name: 'SupplierCatalogues', params: { supplierCatalogueId: props.supplierCatalogueId }})
      } else {
        void $router.push({ name: 'SupplierCatalogues' })
      }
    }

    const saveSupplier = async (sysId, savePayload) => {
      const response = await requestHandler.put(contentType, sysId, savePayload)
      mutate(response)
      setActiveSupplier(sysId)
    }

    function createSupplier (name) {
      $directr.put(contentType, [], { name })
      .then(({ items: [supplier] }) => {
        mutate(supplier, Mutation.create, true)
        setActive(supplier)

        $q.notify({type: 'positive', classes: 'custom-notify success', message: 'New supplier created'})
          return supplier
      }).catch(e => {
        console.error(e)
        $q.notify({ type: 'negative', message: 'Something went wrong wile creating the supplier', classes: 'custom-notify error'})
      })
    }

    const addSupplier = () => {
      $q.dialog({
        title: 'Create a new Supplier',
        message: 'Please enter a supplier name',
        prompt: {
          model: '',
          type: 'text',
          isValid: val => Boolean(val.length),
        },
        cancel: true,
      }).onOk((name) => {
        if (!model.value) return
        const data = Object.fromEntries(Object.keys(model.value).map(k => {
          if ((model.value || {})[k].default === null) {
            return ([k, null])
          }
          return ([k, (model.value || {})[k].default || getTypeDefault((model.value || {})[k].type)])
        }))

        data.name = name

        let result
        if (requestHandler.create) {
          result = requestHandler.create(contentType, data)
        } else {
          result = requestHandler.put(contentType, { endpoint: 'create' }, data)
        }
        result.then(({ items }) => items).then(items => {
          mutate(items, Mutation.create, true)
          setActive(items[0])
        })
      }).onCancel(() => {
        // console.log('>>>> Cancel')
      }).onDismiss(() => {
        // console.log('I am triggered on both OK and Cancel')
      })
    }

    const deleteSupplier = () => {
      const activeSupplier = active.value
      if (!activeSupplier) return
      $q.dialog({
        title: `Delete supplier ${ activeSupplier?.name }?`,
        message: 'Are you sure?',
        cancel: true,
        persistent: false
      }).onOk(() => {
        if (!activeSupplier) return
        const del = requestHandler.remove || requestHandler.delete
        del(activeSupplier.sys.id).then(() => {
          if (!activeSupplier) return
          const activeIndex: number = suppliers.value.findIndex(c => c?.sys?.id === activeSupplier?.sys?.id)
          if (activeIndex + 1 < suppliers.value.length) {
            setActive(suppliers.value[activeIndex + 1])
          } else if (activeIndex > 0) {
            setActive(suppliers.value[activeIndex - 1])
          } else {
            active.value = null
          }
          mutate(activeSupplier, Mutation.remove)
        })
      }).onCancel(() => {
        // console.log('>>>> Cancel')
      }).onDismiss(() => {
        // console.log('I am triggered on both OK and Cancel')
      })
    }

    return {
      ...toRefs(state),
      active,
      loading,
      setActiveSupplier,
      onSupplierCatalogueClick,
      saveSupplier,
      suppliers,
      mutate,
      Mutation,
      requestHandler,
      createSupplier,
      addSupplier,
      deleteSupplier,
      notify: message => {
        $q.notify({ message, position: 'top', classes: 'custom-notify' })
      },
    }
  }
})
